import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonService from '../../../services/common.service';
import { fetchBuyerSupplierRatings } from '../../../services/supplierRating.service';
import SupplierRatingModal from '../../../components/supplierRating/SupplierRatingModal';
import { useFactoryDetailsMaster } from '../../../hooks/useFactoryDetailsMaster';
import { useProductDetailsMaster } from '../../../hooks/useProductDetailsMaster';
import '../../../assets/styles/grn_manifest_list.scss';

export default function GRNManifestList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [manifests, setManifests] = useState([]);
  const [grns, setGrns] = useState([]);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isGRNDetailsModalOpen, setIsGRNDetailsModalOpen] = useState(false);
  const [detailsModalType, setDetailsModalType] = useState(null); // 'approved', 'partially-approved', 'rejected'
  const [ratingModalManifest, setRatingModalManifest] = useState(null);
  
  // Master Data Hooks
  const { shippingTermsOptions, modeOfTransportOptions } = useFactoryDetailsMaster();
  const { attributeUnitOptions } = useProductDetailsMaster();

  // Fetch accepted shipping manifests and GRNs for buyer
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch manifests
        const manifestResponse = await CommonService.get('/shipping-manifest/buyer/my-manifests');
        let manifestsData = [];
        if (manifestResponse && manifestResponse.success && manifestResponse.data) {
          manifestsData = Array.isArray(manifestResponse.data) ? manifestResponse.data : [manifestResponse.data];
        }
        
        // Filter only buyer-accepted manifests.
        // Some records may carry "Accepted" status prematurely; require acceptance evidence fields.
        const acceptedManifests = manifestsData.filter(manifest => {
          const status = (manifest.status || '').toLowerCase().trim();
          if (status !== 'accepted') return false;
          return Boolean(
            manifest.accepted_date ||
            manifest.accepted_by ||
            manifest.actual_arrival_date
          );
        });
        
        // Transform manifests
        const transformedManifests = acceptedManifests.map(manifest => transformManifestData(manifest));
        
        // Fetch GRNs
        const grnResponse = await CommonService.get('/grn/buyer/my-grns');
        let grnsData = [];
        if (grnResponse && grnResponse.success && grnResponse.data) {
          grnsData = Array.isArray(grnResponse.data) ? grnResponse.data : [grnResponse.data];
        }
        
        setGrns(grnsData);
        
        // Match GRNs to manifests and enrich manifest data
        const manifestsWithGRN = transformedManifests.map(manifest => {
          const manifestIdNum = Number(manifest.id);
          const relatedGRN = grnsData.find(grn => Number(grn.manifest_id) === manifestIdNum);
          const purchaseOrderId = Number(relatedGRN?.purchase_order_id || manifest.originalData?.purchase_order_id) || null;
          const sellerCompanyId = Number(relatedGRN?.seller_company_id || manifest.originalData?.seller_company_id) || null;
          const grnId = relatedGRN?.id ? Number(relatedGRN.id) : null;
          return {
            ...manifest,
            grn: relatedGRN || null,
            grnId,
            purchaseOrderId,
            sellerCompanyId,
            grnStatus: getGRNStatus(relatedGRN),
            grnNumber: relatedGRN?.grn_number || null,
            paymentDisplayStatus: getPaymentDisplayStatus(relatedGRN),
            canPay: canShowPayNow(relatedGRN),
            ewayBillNumber: getEWayBillNumber(manifest),
            supplierRating: null,
          };
        });

        const purchaseOrderIds = manifestsWithGRN
          .map((manifest) => manifest.purchaseOrderId)
          .filter((id) => Number.isFinite(id) && id > 0);
        let ratingsByPoId = {};
        if (purchaseOrderIds.length) {
          try {
            const ratings = await fetchBuyerSupplierRatings(purchaseOrderIds);
            ratingsByPoId = Object.fromEntries(
              ratings.map((rating) => [Number(rating.purchaseOrderId), rating])
            );
          } catch (ratingError) {
            console.warn('Could not load supplier ratings:', ratingError);
          }
        }

        setManifests(
          manifestsWithGRN.map((manifest) => ({
            ...manifest,
            supplierRating: manifest.purchaseOrderId ? ratingsByPoId[manifest.purchaseOrderId] || null : null,
          }))
        );
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to fetch data');
        setManifests([]);
        setGrns([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get E-Way Bill Number from manifest documents or invoice
  const getEWayBillNumber = (manifest) => {
    // Check manifest documents for eway_bill
    if (manifest.documents && Array.isArray(manifest.documents)) {
      const ewayBillDoc = manifest.documents.find(doc => 
        doc.document_type === 'eway_bill' || 
        doc.document_type === 'ewaybill' ||
        doc.document_type?.toLowerCase() === 'eway bill'
      );
      if (ewayBillDoc && ewayBillDoc.document_name) {
        // Try to extract E-Way Bill number from document name
        const match = ewayBillDoc.document_name.match(/EWB[-\s]?(\d+)/i);
        if (match) return match[0];
      }
    }
    
    // Check originalData for invoice_id to fetch E-Way Bill
    if (manifest.originalData?.invoice_id) {
      // We'll fetch this asynchronously if needed, for now return null
      // The E-Way Bill can be fetched when viewing details
      return null;
    }
    
    return null;
  };

  const getGRNStatus = (grn) => {
    if (!grn) return 'Pending';
    
    const status = grn.status || '';
    if (status === 'Draft') return 'Draft';
    if (status === 'Pending Approval') return 'GRN Created';
    if (status === 'Partially Approved') return 'Partially Approved';
    if (status === 'Approved') {
      // Check if partially approved (some items rejected) - fallback check for legacy data
      if (grn.items && Array.isArray(grn.items)) {
        const hasRejected = grn.items.some(item => (item.qty_rejected || 0) > 0);
        const hasAccepted = grn.items.some(item => (item.qty_accepted || 0) > 0);
        if (hasRejected && hasAccepted) return 'Partially Approved';
      }
      return 'Approved';
    }
    if (status === 'Rejected') return 'Rejected';
    
    return 'Pending';
  };

  const isGrnAcceptedForPayment = (grn) => {
    if (!grn) return false;
    const status = String(grn.status || '').trim().toLowerCase();
    return status === 'approved' || status === 'partially approved';
  };

  const getPaymentDisplayStatus = (grn) => {
    if (!grn || !isGrnAcceptedForPayment(grn)) return null;
    const paymentStatus = String(grn.payment_status || 'pending').trim().toLowerCase();
    if (paymentStatus === 'paid') return 'Payment Completed';

    const approvalStatus = String(grn.payment_approval_status || 'not_required').trim().toLowerCase();
    if (approvalStatus === 'pending') return 'Payment Approval Pending';
    if (approvalStatus === 'approved') return 'Payment Approved';
    if (approvalStatus === 'rejected') return 'Payment Approval Rejected';
    return null;
  };

  const canShowPayNow = (grn) => {
    if (!grn || !isGrnAcceptedForPayment(grn)) return false;
    if (String(grn.payment_status || '').trim().toLowerCase() === 'paid') return false;
    if (grn.can_pay === true) return true;
    if (grn.can_pay === false) return false;
    const approvalStatus = String(grn.payment_approval_status || 'not_required').trim().toLowerCase();
    return approvalStatus === 'not_required' || approvalStatus === 'approved';
  };

  const getPaymentStatusClass = (label) => {
    const map = {
      'Payment Approval Pending': 'il-status-pending',
      'Payment Approved': 'il-status-accepted',
      'Payment Completed': 'il-status-accepted',
      'Payment Approval Rejected': 'il-status-rejected',
    };
    return map[label] || 'il-status-pending';
  };

  const handlePayNow = (manifest) => {
    const grn = manifest.grn;
    if (!grn?.id || !canShowPayNow(grn)) return;

    const amount =
      grn.payment_amount != null && Number(grn.payment_amount) > 0
        ? Number(grn.payment_amount)
        : manifest.originalData?.grand_total != null
          ? Number(manifest.originalData.grand_total)
          : null;

    const summaryLines = [
      manifest.poNumber && manifest.poNumber !== 'N/A' ? `PO: ${manifest.poNumber}` : null,
      manifest.invoiceNumber && manifest.invoiceNumber !== 'N/A' ? `Invoice: ${manifest.invoiceNumber}` : null,
      manifest.grnNumber ? `GRN: ${manifest.grnNumber}` : null,
    ].filter(Boolean);

    navigate('/payment/gateway', {
      state: {
        paymentData: {
          context: 'marketplace_grn',
          summaryTitle: `Marketplace payment · ${manifest.grnNumber || `GRN #${grn.id}`}`,
          summaryAmount: amount != null ? formatCurrency(amount) : undefined,
          summaryLines: summaryLines.length ? summaryLines : undefined,
        },
        afterSuccessNavigate: '/grn-manifest-list',
        grnPayment: {
          grnId: grn.id,
        },
      },
    });
  };

  // Transform backend manifest data to frontend format
  const transformManifestData = (manifest) => {
    // Map status: "Submitted" -> "Created", "Accepted" -> "Accepted"
    const statusMap = {
      'Submitted': 'Created',
      'submitted': 'Created',
      'Accepted': 'Accepted',
      'accepted': 'Accepted',
      'Draft': 'Draft',
      'draft': 'Draft'
    };
    const displayStatus = statusMap[manifest.status] || manifest.status || 'Created';

    // Determine type based on mode of transport
    const mode = manifest.mode_of_transport || '';
    const type = mode.toLowerCase().includes('air') ? 'AIR' : 'SEA';

    return {
      id: manifest.id,
      manifestNumber: manifest.manifest_number || `MF-${manifest.id}`,
      type: type,
      manifestDate: manifest.manifest_date,
      shippingMethod: type,
      billOfLading: manifest.bill_of_lading || null,
      airWaybill: manifest.airway_bill || null,
      exporter: manifest.from_company_name || 'N/A',
      exporterLocation: `${manifest.from_city || ''}, ${manifest.from_state || ''}, ${manifest.from_country || ''}`.trim(),
      importer: manifest.to_company_name || 'N/A',
      importerLocation: `${manifest.to_city || ''}, ${manifest.to_state || ''}, ${manifest.to_country || ''}`.trim(),
      mode: manifest.mode_of_transport || 'N/A',
      incoterms: manifest.incoterms || 'N/A',
      poNumber: manifest.po_number || 'N/A',
      invoiceNumber: manifest.commercial_invoice_number || manifest.invoice_bill_number || 'N/A',
      invoiceId: manifest.invoice_id || null,
      lrNumber: manifest.lr_number || null,
      deliveryPort: manifest.preferred_delivery_port || 'N/A',
      deliveryPortCode: manifest.delivery_port_reference || null,
      carrier: manifest.carrier_vessel || null,
      vesselOrFlight: manifest.vessel_flight_number || null,
      route: manifest.route || null,
      departureDate: manifest.departure_date || null,
      expectedArrival: manifest.expected_arrival_date || manifest.estimated_delivery_date || null,
      actualArrival: manifest.actual_arrival_date || null,
      finalDestination: manifest.final_destination || null,
      packages: manifest.total_packages || 0,
      weight: manifest.total_weight_kg ? `${manifest.total_weight_kg} kg` : '0 kg',
      status: displayStatus,
      invoiceCount: 1,
      containerCount: 0,
      documentsStatus: 'Docs Pending',
      documents: manifest.documents ? {
        invoice: manifest.documents.some(doc => doc.document_type === 'invoice'),
        packingList: manifest.documents.some(doc => doc.document_type === 'packing_list'),
        ewayBill: manifest.documents.some(doc => doc.document_type === 'eway_bill'),
        qualityCert: manifest.documents.some(doc => doc.document_type === 'quality_certificate'),
        certOfOrigin: manifest.documents.some(doc => doc.document_type === 'certificate_of_origin'),
        otherDocs: manifest.documents.some(doc => doc.document_type === 'other_document')
      } : {},
      documentFiles: manifest.documents ? manifest.documents.reduce((acc, doc) => {
        const typeMap = {
          'invoice': 'invoice',
          'packing_list': 'packingList',
          'eway_bill': 'ewayBill',
          'quality_certificate': 'qualityCert',
          'certificate_of_origin': 'certOfOrigin',
          'other_document': 'otherDocs'
        };
        const key = typeMap[doc.document_type];
        if (key && doc.document_path) {
          acc[key] = { name: doc.document_name || 'Document', type: 'PDF' };
        }
        return acc;
      }, {}) : {},
      lastUpdated: manifest.updated_datetime || manifest.created_datetime || null,
      vehicleDetails: {
        vehicleType: manifest.vehicle_type || null,
        vehicleNumber: manifest.vehicle_number || null,
        vehicleCapacity: manifest.vehicle_capacity || null,
        gpsTrackingId: manifest.gps_tracking_id || null,
        gpsProvider: manifest.gps_provider || null,
        insurancePolicyNo: manifest.insurance_policy_no || null,
        insuranceProvider: manifest.insurance_provider || null,
        driverName: manifest.driver_name || null,
        driverLicenseNo: manifest.driver_license_no || null,
        driverContactNumber: manifest.driver_contact_number || null
      },
      goodsHandling: manifest.goods_handling_requirements ? (
        typeof manifest.goods_handling_requirements === 'string' 
          ? JSON.parse(manifest.goods_handling_requirements)
          : manifest.goods_handling_requirements
      ) : {},
      packageItems: manifest.items ? manifest.items.map(item => ({
        hsnProductCode: item.hsn_product_code || 'N/A',
        productDescription: item.product_description || 'N/A',
        qtyShipped: item.qty_shipped || '0',
        qtyTotal: item.qty_total || '0',
        shippingQty: item.shipping_qty || '0',
        uom: item.uom || 'N/A',
        packageType: item.package_type || 'N/A',
        noPackages: item.no_packages || '0',
        weightPerPkg: item.weight_per_pkg_kg || '0',
        totalWeight: item.total_weight_kg ? `${item.total_weight_kg} kg` : '0 kg'
      })) : [],
      additionalNotes: manifest.additional_notes || null,
      totals: {
        packages: manifest.total_packages || 0,
        grossWeight: manifest.total_weight_kg ? `${manifest.total_weight_kg} kg` : '0 kg',
        netWeight: manifest.total_weight_kg ? `${manifest.total_weight_kg} kg` : '0 kg'
      },
      // Acceptance details from backend
      acceptanceDetails: (manifest.accepted_date || manifest.accepted_by || manifest.acceptance_notes || manifest.inspection_checklist) ? (() => {
        // Get inspection checklist from separate column
        let inspectionChecklist = {};
        if (manifest.inspection_checklist) {
          if (typeof manifest.inspection_checklist === 'string') {
            try {
              inspectionChecklist = JSON.parse(manifest.inspection_checklist);
            } catch (e) {
              console.warn('Failed to parse inspection_checklist:', e);
            }
          } else if (typeof manifest.inspection_checklist === 'object') {
            inspectionChecklist = manifest.inspection_checklist;
          }
        }
        
        // Fallback: Try to parse from acceptance_notes for old records
        let additionalNotes = manifest.acceptance_notes || null;
        if (!manifest.inspection_checklist && manifest.acceptance_notes) {
          try {
            const parsed = JSON.parse(manifest.acceptance_notes);
            if (parsed && typeof parsed === 'object') {
              if (parsed.inspectionChecklist && typeof parsed.inspectionChecklist === 'object') {
                inspectionChecklist = parsed.inspectionChecklist;
              }
              if (parsed.notes !== undefined) {
                additionalNotes = parsed.notes || null;
              }
            }
          } catch (e) {
            additionalNotes = manifest.acceptance_notes;
          }
        }

        // Find POD document from documents array
        let podFile = null;
        if (manifest.documents && Array.isArray(manifest.documents)) {
          const podDoc = manifest.documents.find(doc => 
            doc.document_type === 'pod' || 
            doc.document_type === 'proof_of_delivery' ||
            doc.document_type?.toLowerCase() === 'pod'
          );
          if (podDoc) {
            podFile = {
              name: podDoc.document_name || 'Proof of Delivery',
              path: podDoc.document_path,
              uploadedAt: podDoc.created_datetime || podDoc.updated_datetime
            };
          }
        }

        return {
          dateReceived: manifest.actual_arrival_date || manifest.accepted_date || null,
          acceptedDate: manifest.accepted_date || null,
          receivedBy: manifest.accepted_by || null,
          packagesReceived: manifest.total_packages?.toString() || null,
          condition: 'Good Condition',
          additionalNotes: additionalNotes,
          inspectionChecklist: inspectionChecklist,
          podFile: podFile
        };
      })() : null,
      originalData: manifest
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const canRateSupplier = (manifest) => {
    if (!manifest?.purchaseOrderId || !manifest?.grn) return false;
    if (manifest.grnStatus !== 'Approved' && manifest.grnStatus !== 'Partially Approved') return false;
    return !manifest.supplierRating;
  };

  const handleRatingSubmitted = (savedRating) => {
    const purchaseOrderId = Number(savedRating?.purchaseOrderId);
    if (!Number.isFinite(purchaseOrderId) || purchaseOrderId <= 0) return;
    setManifests((current) =>
      current.map((manifest) =>
        manifest.purchaseOrderId === purchaseOrderId
          ? { ...manifest, supplierRating: savedRating }
          : manifest
      )
    );
    setRatingModalManifest(null);
  };

  // Helper function to get unit label
  const getUnitLabel = (unitValue) => {
    if (!unitValue) return '';
    if (!attributeUnitOptions || attributeUnitOptions.length === 0) return unitValue;
    
    const unitValueNum = isNaN(unitValue) ? null : Number(unitValue);
    const foundByValue = attributeUnitOptions.find(opt => {
      if (String(opt.value) === String(unitValue)) return true;
      if (unitValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === unitValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || unitValue;
    }
    
    const foundByLabel = attributeUnitOptions.find(opt => 
      opt.label === String(unitValue) || 
      opt.label?.toLowerCase() === String(unitValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return unitValue;
  };

  // Helper function to format currency
  const formatCurrency = (amount, currencyCode = 'INR') => {
    if (!amount && amount !== 0) return 'N/A';
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return 'N/A';
    
    const formatted = numAmount.toLocaleString('en-IN', {
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
    
    // Get currency symbol (simplified - you can enhance this)
    const symbol = currencyCode === 'USD' ? '$' : currencyCode === 'EUR' ? '€' : '₹';
    
    return `${symbol}${formatted}`;
  };

  // Helper function to get mode of transport label
  const getModeOfTransportLabel = (modeValue) => {
    if (!modeValue) return 'N/A';
    if (!modeOfTransportOptions || modeOfTransportOptions.length === 0) return modeValue;
    
    const modeValueNum = isNaN(modeValue) ? null : Number(modeValue);
    const foundByValue = modeOfTransportOptions.find(opt => {
      if (String(opt.value) === String(modeValue)) return true;
      if (modeValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === modeValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || modeValue;
    }
    
    const foundByLabel = modeOfTransportOptions.find(opt => 
      opt.label === String(modeValue) || 
      opt.label?.toLowerCase() === String(modeValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return modeValue;
  };

  // Follow-up action options
  const followUpActionOptions = [
    { value: '', label: 'Select action...' },
    { value: 'RETURN_TO_SUPPLIER', label: 'Return to Supplier' },
    { value: 'REPLACEMENT_REQUIRED', label: 'Replacement Required' },
    { value: 'CREDIT_NOTE', label: 'Issue Credit Note' },
    { value: 'INVESTIGATE', label: 'Investigate Further' },
    { value: 'ACCEPT_WITH_DISCOUNT', label: 'Accept with Discount' },
    { value: 'OTHER', label: 'Other' }
  ];

  // Helper function to get follow-up action label from value
  const getFollowUpActionLabel = (value) => {
    if (!value) return null;
    const option = followUpActionOptions.find(opt => opt.value === value);
    return option ? option.label : value;
  };

  // Helper function to get incoterms label
  const getIncotermsLabel = (incotermsValue) => {
    if (!incotermsValue) return 'N/A';
    if (!shippingTermsOptions || shippingTermsOptions.length === 0) return incotermsValue;
    
    const incotermsValueNum = isNaN(incotermsValue) ? null : Number(incotermsValue);
    const foundByValue = shippingTermsOptions.find(opt => {
      if (String(opt.value) === String(incotermsValue)) return true;
      if (incotermsValueNum !== null && !isNaN(opt.value)) {
        return Number(opt.value) === incotermsValueNum;
      }
      return false;
    });
    
    if (foundByValue) {
      return foundByValue.label || incotermsValue;
    }
    
    const foundByLabel = shippingTermsOptions.find(opt => 
      opt.label === String(incotermsValue) || 
      opt.label?.toLowerCase() === String(incotermsValue).toLowerCase()
    );
    if (foundByLabel) {
      return foundByLabel.label;
    }
    
    return incotermsValue;
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'Pending': 'sml-status-pending',
      'Draft': 'sml-status-draft',
      'GRN Created': 'sml-status-in-transit',
      'Approved': 'sml-status-accepted',
      'Partially Approved': 'sml-status-partial',
      'Rejected': 'sml-status-rejected'
    };
    return statusMap[status] || 'sml-status-pending';
  };

  const renderStatusIcon = (status) => {
    if (status === 'Approved') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21.801 10A10 10 0 1 1 17 3.335"></path>
          <path d="m9 11 3 3L22 4"></path>
        </svg>
      );
    }
    if (status === 'Partially Approved') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
        </svg>
      );
    }
    if (status === 'Rejected') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="15" y1="9" x2="9" y2="15"></line>
          <line x1="9" y1="9" x2="15" y2="15"></line>
        </svg>
      );
    }
    if (status === 'Draft') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
          <polyline points="10 9 9 9 8 9"></polyline>
        </svg>
      );
    }
    return null;
  };

  const renderStatusBadge = (status) => (
    <span className={`sml-status-tag ${getStatusClass(status)}`}>
      {renderStatusIcon(status)}
      {status}
    </span>
  );

  const filteredManifests = manifests.filter(manifest => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const searchFields = [
      manifest.manifestNumber,
      manifest.exporter,
      manifest.importer,
      manifest.billOfLading,
      manifest.airWaybill,
      manifest.poNumber,
      manifest.invoiceNumber,
      manifest.lrNumber,
      manifest.deliveryPortCode
    ].filter(Boolean);

    const matchesSearch = normalizedQuery === '' 
      ? true 
      : searchFields.some(field => field.toLowerCase().includes(normalizedQuery));
    
    return matchesSearch;
  });

  const handleGenerateGRN = (manifest) => {
    // Navigate to GRN generation page with manifest and acceptance data
    navigate('/buyer-goods-receipt-generate', {
      state: {
        manifest: manifest,
        acceptanceForm: manifest.acceptanceDetails || {}
      }
    });
  };

  const handleEditGRN = (manifest) => {
    // Navigate to GRN generation page with existing GRN data
    if (manifest.grn && manifest.grn.id) {
      const grnId = manifest.grn.id;
      console.log('Navigating to edit GRN:', { grnId, hasManifest: !!manifest, hasGrn: !!manifest.grn });
      
      navigate(`/buyer-goods-receipt-generate?grnId=${grnId}`, {
        state: {
          manifest: manifest,
          acceptanceForm: manifest.acceptanceDetails || {},
          grnId: grnId,
          editMode: true
        },
        replace: false
      });
    } else {
      console.error('Cannot edit GRN: manifest.grn or manifest.grn.id is missing', manifest);
      toast.error('GRN data not found. Cannot edit.');
    }
  };

  const handleViewDetails = async (manifestId, type = null) => {
    try {
      // Fetch full manifest details with items
      const response = await CommonService.get(`/shipping-manifest/${manifestId}`);
      if (response && response.success && response.data) {
        // Store the raw backend data for the modal (it expects backend structure)
        const rawManifest = response.data;
        const manifest = manifests.find(m => m.id === manifestId);
        
        // If viewing GRN details (approved/partially-approved/rejected), fetch full GRN data
        if (type && manifest?.grn) {
          try {
            const grnResponse = await CommonService.get(`/grn/${manifest.grn.id}`);
            if (grnResponse && grnResponse.success && grnResponse.data) {
              setSelectedGRN(grnResponse.data);
              setSelectedManifest(rawManifest); // Use raw backend data
              setDetailsModalType(type);
              setIsDetailsModalOpen(true);
            } else {
              setSelectedManifest(rawManifest);
              setIsModalOpen(true);
            }
          } catch (grnError) {
            console.error('Error fetching GRN details:', grnError);
            setSelectedManifest(rawManifest);
            setIsModalOpen(true);
          }
        } else if (manifest?.grn && manifest.grn.id) {
          // If there's a GRN but no specific type, show comprehensive GRN details
          try {
            const grnResponse = await CommonService.get(`/grn/${manifest.grn.id}`);
            if (grnResponse && grnResponse.success && grnResponse.data) {
              setSelectedGRN(grnResponse.data);
              setSelectedManifest(rawManifest);
              setIsGRNDetailsModalOpen(true);
            } else {
              // Fallback to manifest view
              setSelectedManifest(rawManifest);
              setIsModalOpen(true);
            }
          } catch (grnError) {
            console.error('Error fetching GRN details:', grnError);
            // Fallback to manifest view
            setSelectedManifest(rawManifest);
            setIsModalOpen(true);
          }
        } else {
          // For regular view without GRN, use raw backend data
          setSelectedManifest(rawManifest);
          setIsModalOpen(true);
        }
      } else {
        // Fallback: try to get from list
        const manifest = manifests.find(item => item.id === manifestId);
        if (manifest && manifest.originalData) {
          setSelectedManifest(manifest.originalData);
          setIsModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Error fetching manifest details:', error);
      toast.error('Failed to fetch manifest details');
      // Fallback: try to get from list
      const manifest = manifests.find(item => item.id === manifestId);
      if (manifest && manifest.originalData) {
        setSelectedManifest(manifest.originalData);
        setIsModalOpen(true);
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsDetailsModalOpen(false);
    setIsGRNDetailsModalOpen(false);
    setSelectedManifest(null);
    setSelectedGRN(null);
    setDetailsModalType(null);
  };

  // Get primary and secondary numbers for display
  const getDisplayNumbers = (manifest) => {
    if (manifest.grnNumber) {
      return {
        primary: manifest.grnNumber,
        primaryLabel: 'GRN Number',
        secondary: manifest.manifestNumber,
        secondaryLabel: 'Manifest Number'
      };
    } else {
      return {
        primary: manifest.manifestNumber,
        primaryLabel: 'Manifest Number',
        secondary: manifest.ewayBillNumber || null,
        secondaryLabel: manifest.ewayBillNumber ? 'E-Way Bill Number' : null
      };
    }
  };

  return (
    <div className="invoice-list">
      <div className="il-main-content">
        {/* Header Section */}
        <div className="il-header">
          <div className="il-header-left">
            <nav aria-label="breadcrumb" className="il-breadcrumb">
              <ol className="il-breadcrumb-list">
                <li className="il-breadcrumb-item">
                  <Link to="/dashboard/buyer" className="il-breadcrumb-link">Home</Link>
                </li>
                <li className="il-breadcrumb-item il-breadcrumb-active" aria-current="page">
                  Generate GRN
                </li>
              </ol>
            </nav>
            <h1 className="il-main-title">Generate GRN</h1>
            <p className="il-subtitle">Select an accepted shipping manifest to generate Goods Received Note</p>
          </div>
          <div className="il-header-right">
            <button className="il-back-btn" onClick={() => navigate('/dashboard/buyer')}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m12 19-7-7 7-7"></path>
                <path d="M19 12H5"></path>
              </svg>
              Back
            </button>
          </div>
        </div>

        {/* Search Section */}
        <div className="il-search-filter-section">
          <div className="il-search-header">
            <h2 className="il-search-title">Search Accepted Shipping Manifests</h2>
          </div>
          <div className="il-search-filter-row">
            <div className="il-search-wrapper">
              <svg 
                className="il-search-icon" 
                xmlns="http://www.w3.org/2000/svg" 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                className="il-search-input"
                placeholder="Search by manifest, PO, invoice, exporter, or AWB/BL..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Manifest Cards */}
        <div className="il-invoices-container">
          {loading ? (
            <div className="il-empty-state">
              <p>Loading accepted shipping manifests...</p>
            </div>
          ) : filteredManifests.length === 0 ? (
            <div className="il-empty-state">
              <p>No accepted shipping manifests found.</p>
            </div>
          ) : (
            filteredManifests.map((manifest) => {
              const numbers = getDisplayNumbers(manifest);
              return (
                <div key={manifest.id} className="il-invoice-card">
                  <div className="il-card-content-wrapper">
                    <div className="il-card-main-row">
                      <div className="il-card-left-section">
                        <div className="il-invoice-header-row">
                          <div className="il-invoice-header-left">
                            <svg 
                              className="il-document-icon"
                              xmlns="http://www.w3.org/2000/svg" 
                              width="24" 
                              height="24" 
                              viewBox="0 0 24 24" 
                              fill="none" 
                              stroke="currentColor" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            >
                              <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                              <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                              <path d="M10 9H8"></path>
                              <path d="M16 13H8"></path>
                              <path d="M16 17H8"></path>
                            </svg>
                            <div className="il-invoice-title-group">
                              <div className="il-title-row">
                                <h3 className="il-invoice-number">
                                  {numbers.primary}
                                </h3>
                                <span className={`il-status-badge ${
                                  manifest.grnStatus === 'Pending' ? 'il-status-pending' :
                                  manifest.grnStatus === 'Draft' ? 'il-status-draft' :
                                  manifest.grnStatus === 'GRN Created' ? 'il-status-accepted' :
                                  manifest.grnStatus === 'Approved' ? 'il-status-accepted' :
                                  manifest.grnStatus === 'Partially Approved' ? 'il-status-partial' :
                                  manifest.grnStatus === 'Rejected' ? 'il-status-rejected' : 'il-status-pending'
                                }`}>
                                  {manifest.grnStatus || 'Pending'}
                                </span>
                                {manifest.paymentDisplayStatus ? (
                                  <span className={`il-status-badge ${getPaymentStatusClass(manifest.paymentDisplayStatus)}`}>
                                    {manifest.paymentDisplayStatus}
                                  </span>
                                ) : null}
                              </div>
                              <p className="il-invoice-number-subtitle">
                                {numbers.secondary ? (
                                  <>
                                    {numbers.secondaryLabel}: <span className="il-invoice-number-value">{numbers.secondary}</span>
                                    {' | '}
                                    Invoice: <span className="il-invoice-number-value">{manifest.invoiceNumber}</span>
                                  </>
                                ) : (
                                  <>
                                    Manifest: <span className="il-invoice-number-value">{manifest.manifestNumber}</span>
                                    {' | '}
                                    Invoice: <span className="il-invoice-number-value">{manifest.invoiceNumber}</span>
                                  </>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="il-card-actions">
                            <button 
                              className="il-action-btn il-btn-view"
                              onClick={() => handleViewDetails(manifest.id)}
                            >
                              <svg 
                                xmlns="http://www.w3.org/2000/svg" 
                                width="16" 
                                height="16" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                              View
                            </button>
                            
                            {/* Show Generate GRN button only for Pending status */}
                            {(manifest.grnStatus === 'Pending' || !manifest.grn) && (
                              <button 
                                className="il-action-btn il-btn-eway"
                                onClick={() => handleGenerateGRN(manifest)}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
                                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
                                  <line x1="12" y1="22.08" x2="12" y2="12"></line>
                                </svg>
                                Generate GRN
                              </button>
                            )}
                            
                            {/* Show Edit GRN button for Draft status */}
                            {manifest.grnStatus === 'Draft' && manifest.grn && (
                              <button 
                                className="il-action-btn il-btn-edit"
                                onClick={() => handleEditGRN(manifest)}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                </svg>
                                Edit GRN
                              </button>
                            )}
                            
                            {/* Show View Approved Details button for Approved status */}
                            {manifest.grnStatus === 'Approved' && manifest.grn && (
                              <button 
                                className="il-action-btn il-btn-acceptance"
                                onClick={() => handleViewDetails(manifest.id, 'approved')}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                                View Approved Details
                              </button>
                            )}
                            
                            {/* Show View Partially Approved Details button for Partially Approved status */}
                            {manifest.grnStatus === 'Partially Approved' && manifest.grn && (
                              <button 
                                className="il-action-btn il-btn-acceptance"
                                onClick={() => handleViewDetails(manifest.id, 'partially-approved')}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                </svg>
                                View Partially Approved Details
                              </button>
                            )}

                            {(manifest.grnStatus === 'Approved' || manifest.grnStatus === 'Partially Approved') &&
                              manifest.grn &&
                              manifest.canPay ? (
                              <button
                                className="il-action-btn il-btn-eway"
                                onClick={() => handlePayNow(manifest)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <rect width="20" height="14" x="2" y="5" rx="2" />
                                  <line x1="2" x2="22" y1="10" y2="10" />
                                </svg>
                                Pay Now
                              </button>
                            ) : null}

                            {canRateSupplier(manifest) ? (
                              <button
                                type="button"
                                className="il-action-btn il-btn-acceptance"
                                onClick={() => setRatingModalManifest(manifest)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                </svg>
                                Rate Supplier
                              </button>
                            ) : null}

                            {manifest.supplierRating ? (
                              <span className="il-action-btn il-btn-view" style={{ cursor: 'default' }}>
                                Rated {manifest.supplierRating.rating}/5
                              </span>
                            ) : null}
                            
                            {/* Show View Rejected Details button for Rejected status */}
                            {manifest.grnStatus === 'Rejected' && manifest.grn && (
                              <button 
                                className="il-action-btn il-btn-reject"
                                onClick={() => handleViewDetails(manifest.id, 'rejected')}
                              >
                                <svg 
                                  xmlns="http://www.w3.org/2000/svg" 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <line x1="15" y1="9" x2="9" y2="15"></line>
                                  <line x1="9" y1="9" x2="15" y2="15"></line>
                                </svg>
                                View Rejected Details
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="il-details-grid">
                          <div className="il-grid-item">
                            <p className="il-detail-label">Exporter</p>
                            <p className="il-detail-value" title={manifest.exporter}>{manifest.exporter}</p>
                            <p className="il-detail-subtext">{manifest.exporterLocation}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Importer</p>
                            <p className="il-detail-value" title={manifest.importer}>{manifest.importer}</p>
                            <p className="il-detail-subtext">{manifest.importerLocation}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Mode / Incoterms</p>
                            <p className="il-detail-value">{getModeOfTransportLabel(manifest.mode)} / {getIncotermsLabel(manifest.incoterms)}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">PO Number</p>
                            <p className="il-detail-value">{manifest.poNumber}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Packages</p>
                            <p className="il-detail-value">{manifest.packages}</p>
                          </div>
                          <div className="il-grid-item">
                            <p className="il-detail-label">Weight</p>
                            <p className="il-detail-value">{manifest.weight}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* View Details Modal */}
        {isModalOpen && selectedManifest && !isDetailsModalOpen && !isGRNDetailsModalOpen && (
          <div className="sm-preview-modal-overlay" onClick={handleCloseModal}>
            <div className="sm-preview-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sm-preview-modal-header">
                <div className="sm-preview-modal-header-left">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="sm-preview-icon">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <div>
                    <h2 className="sm-preview-modal-title">Shipping Manifest Details</h2>
                    <p className="sm-preview-modal-subtitle">View complete shipping manifest information</p>
                  </div>
                </div>
                <button className="sm-preview-modal-close" onClick={handleCloseModal}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="sm-preview-modal-body">
                {/* Manifest Information */}
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title">Manifest Information</h3>
                  <div className="sm-preview-grid">
                    <div className="sm-preview-item">
                      <label>Manifest Number</label>
                      <p>{selectedManifest.manifest_number || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>LR Number</label>
                      <p>{selectedManifest.lr_number || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Manifest Date</label>
                      <p>{selectedManifest.manifest_date ? formatDate(selectedManifest.manifest_date) : 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Status</label>
                      <p>{selectedManifest.status || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                
                {/* From (Exporter/Shipper) */}
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title sm-preview-section-title-orange">From (Exporter/Shipper)</h3>
                  <div className="sm-preview-grid">
                    <div className="sm-preview-item">
                      <label>Company / Shipper Name</label>
                      <p>{selectedManifest.from_company_name || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Contact Person</label>
                      <p>{selectedManifest.from_contact_person || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>GSTIN</label>
                      <p>{selectedManifest.from_gstin || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>IEC / Export Code</label>
                      <p>{selectedManifest.from_iec || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Address</label>
                      <p>{selectedManifest.from_address_line1 || 'N/A'} {selectedManifest.from_address_line2 && selectedManifest.from_address_line2}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Location</label>
                      <p>{selectedManifest.from_city || 'N/A'}, {selectedManifest.from_state || 'N/A'}, {selectedManifest.from_country || 'N/A'} - {selectedManifest.from_postal_code || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Email</label>
                      <p>{selectedManifest.from_email || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Phone</label>
                      <p>{selectedManifest.from_phone || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                
                {/* To (Consignee/Importer) */}
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title sm-preview-section-title-green">To (Consignee/Importer)</h3>
                  <div className="sm-preview-grid">
                    <div className="sm-preview-item">
                      <label>Company / Consignee Name</label>
                      <p>{selectedManifest.to_company_name || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Contact Person</label>
                      <p>{selectedManifest.to_contact_person || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Tax ID / Import Code</label>
                      <p>{selectedManifest.to_tax_id || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Email</label>
                      <p>{selectedManifest.to_email || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Address</label>
                      <p>{selectedManifest.to_address_line1 || 'N/A'} {selectedManifest.to_address_line2 && selectedManifest.to_address_line2}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Location</label>
                      <p>{selectedManifest.to_city || 'N/A'}, {selectedManifest.to_state || 'N/A'}, {selectedManifest.to_country || 'N/A'} - {selectedManifest.to_postal_code || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Phone</label>
                      <p>{selectedManifest.to_phone || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Order Reference Details */}
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title sm-preview-section-title-blue">Order Reference Details</h3>
                  <div className="sm-preview-grid">
                    <div className="sm-preview-item">
                      <label>PO Number</label>
                      <p>{selectedManifest.po_number || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Order Date</label>
                      <p>{selectedManifest.order_date ? formatDate(selectedManifest.order_date) : 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Currency</label>
                      <p>{selectedManifest.currency || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Subtotal Amount</label>
                      <p>{selectedManifest.subtotal_amount ? formatCurrency(selectedManifest.subtotal_amount, selectedManifest.currency || 'INR') : 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Buyer Name</label>
                      <p>{selectedManifest.buyer_name || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Supplier Name</label>
                      <p>{selectedManifest.supplier_name || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Commercial Invoice Number</label>
                      <p>{selectedManifest.commercial_invoice_number || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Shipment Details */}
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title sm-preview-section-title-purple">Shipment Details</h3>
                  <div className="sm-preview-grid">
                    <div className="sm-preview-item">
                      <label>Preferred Delivery Port</label>
                      <p>{selectedManifest.preferred_delivery_port || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Delivery Port Reference</label>
                      <p>{selectedManifest.delivery_port_reference || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Invoice/Bill Number</label>
                      <p>{selectedManifest.invoice_bill_number || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>E-way Bill Number</label>
                      <p>{(() => {
                        // Try to get E-Way Bill number from documents or original data
                        if (selectedManifest.documents && Array.isArray(selectedManifest.documents)) {
                          const ewayBillDoc = selectedManifest.documents.find(doc => 
                            doc.document_type === 'eway_bill' || 
                            doc.document_type === 'ewaybill'
                          );
                          if (ewayBillDoc && ewayBillDoc.document_name) {
                            const match = ewayBillDoc.document_name.match(/EWB[-\s]?(\d+)/i);
                            if (match) return match[0];
                          }
                        }
                        return 'N/A';
                      })()}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Mode of Transport</label>
                      <p>{(() => {
                        if (!selectedManifest.mode_of_transport) return 'N/A';
                        if (modeOfTransportOptions && modeOfTransportOptions.length > 0) {
                          const found = modeOfTransportOptions.find(opt => 
                            String(opt.value) === String(selectedManifest.mode_of_transport) ||
                            opt.label === selectedManifest.mode_of_transport
                          );
                          return found ? found.label : selectedManifest.mode_of_transport;
                        }
                        return selectedManifest.mode_of_transport;
                      })()}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Incoterms</label>
                      <p>{(() => {
                        if (!selectedManifest.incoterms) return 'N/A';
                        if (shippingTermsOptions && shippingTermsOptions.length > 0) {
                          const found = shippingTermsOptions.find(opt => 
                            String(opt.value) === String(selectedManifest.incoterms) ||
                            opt.label === selectedManifest.incoterms
                          );
                          return found ? found.label : selectedManifest.incoterms;
                        }
                        return selectedManifest.incoterms;
                      })()}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Estimated Delivery Date</label>
                      <p>{selectedManifest.estimated_delivery_date ? formatDate(selectedManifest.estimated_delivery_date) : 'N/A'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Vehicle & Driver Details */}
                <div className="sm-preview-section">
                  <h3 className="sm-preview-section-title sm-preview-section-title-indigo">Vehicle & Driver Details</h3>
                  <div className="sm-preview-grid">
                    <div className="sm-preview-item">
                      <label>Vehicle Type</label>
                      <p>{selectedManifest.vehicle_type || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Vehicle Number</label>
                      <p>{selectedManifest.vehicle_number || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Vehicle Capacity</label>
                      <p>{selectedManifest.vehicle_capacity || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>GPS Tracking ID</label>
                      <p>{selectedManifest.gps_tracking_id || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>GPS Provider</label>
                      <p>{selectedManifest.gps_provider || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Insurance Policy No.</label>
                      <p>{selectedManifest.insurance_policy_no || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Insurance Provider</label>
                      <p>{selectedManifest.insurance_provider || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Driver Name</label>
                      <p>{selectedManifest.driver_name || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Driver License No.</label>
                      <p>{selectedManifest.driver_license_no || 'N/A'}</p>
                    </div>
                    <div className="sm-preview-item">
                      <label>Driver Contact Number</label>
                      <p>{selectedManifest.driver_contact_number || 'N/A'}</p>
                    </div>
                  </div>
                </div>
                
                {/* Items & Packages Details */}
                {selectedManifest.items && selectedManifest.items.length > 0 && (
                  <div className="sm-preview-section">
                    <h3 className="sm-preview-section-title sm-preview-section-title-orange">Items & Packages Details</h3>
                    <div className="sm-preview-table-wrapper">
                      <table className="sm-preview-table">
                        <thead>
                          <tr>
                            <th>HSN/Product Code</th>
                            <th>Product Description</th>
                            <th>Qty Shipped</th>
                            <th>Qty Total</th>
                            <th>Shipping Qty</th>
                            <th>UOM</th>
                            <th>Package Type</th>
                            <th>No. Packages</th>
                            <th>Weight/Pkg (kg)</th>
                            <th>Total Weight</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedManifest.items.map((item, index) => (
                            <tr key={index}>
                              <td>{item.hsn_product_code || 'N/A'}</td>
                              <td>{item.product_description || 'N/A'}</td>
                              <td>{item.qty_shipped || 'N/A'}</td>
                              <td>{item.qty_total || 'N/A'}</td>
                              <td>{item.shipping_qty || 'N/A'}</td>
                              <td>{getUnitLabel(item.uom) || '-'}</td>
                              <td>{item.package_type || 'N/A'}</td>
                              <td>{item.no_packages || 'N/A'}</td>
                              <td>{item.weight_per_pkg_kg || 'N/A'}</td>
                              <td>{item.total_weight_kg || 'N/A'} kg</td>
                            </tr>
                          ))}
                          <tr className="sm-preview-total-row">
                            <td colSpan="1"></td>
                            <td className="sm-totals-label">Totals:</td>
                            <td colSpan="5"></td>
                            <td className="sm-total-value">{selectedManifest.total_packages || 0}</td>
                            <td colSpan="1"></td>
                            <td className="sm-total-value">{selectedManifest.total_weight_kg || 0} kg</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                
                {/* Goods Handling Requirements */}
                {selectedManifest.goods_handling_requirements && (
                  <div className="sm-preview-section">
                    <h3 className="sm-preview-section-title sm-preview-section-title-red">Goods Handling Requirements</h3>
                    <div className="sm-preview-grid">
                      {(() => {
                        const requirements = typeof selectedManifest.goods_handling_requirements === 'string' 
                          ? JSON.parse(selectedManifest.goods_handling_requirements) 
                          : selectedManifest.goods_handling_requirements;
                        return (
                          <>
                            <div className="sm-preview-item">
                              <label>Hazardous Material</label>
                              <p>{requirements.hazardousMaterial ? 'Yes' : 'No'}</p>
                            </div>
                            {requirements.hazardousMaterial && (
                              <>
                                <div className="sm-preview-item">
                                  <label>Hazard Class</label>
                                  <p>{requirements.hazardClass || 'N/A'}</p>
                                </div>
                                <div className="sm-preview-item">
                                  <label>UN Number</label>
                                  <p>{requirements.unNumber || 'N/A'}</p>
                                </div>
                              </>
                            )}
                            <div className="sm-preview-item">
                              <label>Temperature Controlled</label>
                              <p>{requirements.temperatureControlled ? 'Yes' : 'No'}</p>
                            </div>
                            {requirements.temperatureControlled && (
                              <>
                                <div className="sm-preview-item">
                                  <label>Min Temperature</label>
                                  <p>{requirements.minTemperature || 'N/A'}</p>
                                </div>
                                <div className="sm-preview-item">
                                  <label>Max Temperature</label>
                                  <p>{requirements.maxTemperature || 'N/A'}</p>
                                </div>
                              </>
                            )}
                            <div className="sm-preview-item">
                              <label>Fragile</label>
                              <p>{requirements.fragile ? 'Yes' : 'No'}</p>
                            </div>
                            {requirements.specialHandling && (
                              <div className="sm-preview-item sm-preview-item-full">
                                <label>Special Handling</label>
                                <p>{requirements.specialHandling}</p>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
                
                {/* Additional Notes */}
                {selectedManifest.additional_notes && (
                  <div className="sm-preview-section">
                    <h3 className="sm-preview-section-title sm-preview-section-title-gray">Additional Notes / Remarks</h3>
                    <p className="sm-preview-text">{selectedManifest.additional_notes}</p>
                  </div>
                )}
              </div>
              <div className="sm-preview-modal-footer">
                {(() => {
                  // Find the manifest from the list to get GRN status
                  const manifestFromList = manifests.find(m => m.id === selectedManifest.id);
                  return (
                    <>
                      {(manifestFromList && (manifestFromList.grnStatus === 'Pending' || !manifestFromList.grn)) && (
                        <button
                          className="sm-btn sm-btn-primary"
                          onClick={() => {
                            handleCloseModal();
                            handleGenerateGRN(manifestFromList);
                          }}
                        >
                          Generate GRN
                        </button>
                      )}
                      {manifestFromList && manifestFromList.grnStatus === 'Draft' && manifestFromList.grn && (
                        <button
                          className="sm-btn sm-btn-primary"
                          onClick={() => {
                            handleCloseModal();
                            handleEditGRN(manifestFromList);
                          }}
                        >
                          Edit GRN
                        </button>
                      )}
                      <button className="sm-btn sm-btn-secondary" onClick={handleCloseModal}>Close</button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* View Approved/Partially Approved/Rejected Details Modal */}
        {isDetailsModalOpen && selectedGRN && selectedManifest && !isGRNDetailsModalOpen && (
          <div className="grn-manifest-list-modal-wrapper">
            <div className="grn-generate">
              <div className="grn-preview-modal-overlay" onClick={handleCloseModal}>
                <div 
                  className="grn-preview-modal" 
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="grn-details-title"
                  onClick={(e) => e.stopPropagation()}
                  tabIndex="-1"
                >
                  <button 
                    className="grn-preview-modal-close" 
                    onClick={handleCloseModal}
                    aria-label="Close GRN details"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 6 6 18"></path>
                      <path d="m6 6 12 12"></path>
                    </svg>
                  </button>

                  <div className="grn-preview-modal-header">
                    <div>
                      <h2 id="grn-details-title" className="grn-preview-modal-title">
                        {detailsModalType === 'approved' && 'Approved GRN Details'}
                        {detailsModalType === 'partially-approved' && 'Partially Approved GRN Details'}
                        {detailsModalType === 'rejected' && 'Rejected GRN Details'}
                      </h2>
                      <p className="grn-preview-modal-subtitle">
                        View GRN {detailsModalType === 'approved' ? 'approval' : detailsModalType === 'partially-approved' ? 'partial approval' : 'rejection'} information
                      </p>
                    </div>
                  </div>

                  <div className="grn-preview-content" id="grn-details-content">
                    {/* GRN Information */}
                    <div className="grn-preview-card grn-preview-card-blue">
                      <h3 className="grn-preview-card-title grn-preview-card-title-blue">GRN Information</h3>
                      <div className="grn-preview-card-content">
                        <div className="grn-preview-info-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                          <div>
                            <p className="grn-preview-label">GRN Number</p>
                            <p className="grn-preview-value">{selectedGRN.grn_number || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="grn-preview-label">GRN Date</p>
                            <p className="grn-preview-value">{formatDate(selectedGRN.grn_date) || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="grn-preview-label">Status</p>
                            <p className="grn-preview-value">{selectedGRN.status || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="grn-preview-label">PO Number</p>
                            <p className="grn-preview-value">{selectedGRN.po_number || selectedManifest.po_number || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Approval Details */}
                    {detailsModalType === 'approved' && (
                      <div className="grn-preview-card grn-preview-card-green">
                        <h3 className="grn-preview-card-title grn-preview-card-title-green">Approval Details</h3>
                        <div className="grn-preview-card-content">
                          <div className="grn-preview-info-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                            <div>
                              <p className="grn-preview-label">Approved By</p>
                              <p className="grn-preview-value">{selectedGRN.approved_by || 'N/A'}</p>
                            </div>
                            {selectedGRN.approved_by_designation && (
                              <div>
                                <p className="grn-preview-label">Designation</p>
                                <p className="grn-preview-value">{selectedGRN.approved_by_designation}</p>
                              </div>
                            )}
                            <div>
                              <p className="grn-preview-label">Approved Date</p>
                              <p className="grn-preview-value">{formatDate(selectedGRN.approved_date) || 'N/A'}</p>
                            </div>
                            {selectedGRN.follow_up_action && (
                              <div>
                                <p className="grn-preview-label">Follow-up Action</p>
                                <p className="grn-preview-value">{getFollowUpActionLabel(selectedGRN.follow_up_action)}</p>
                              </div>
                            )}
                            {selectedGRN.approval_notes && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <p className="grn-preview-label">Approval Notes</p>
                                <p className="grn-preview-value">{selectedGRN.approval_notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Partial Approval Details */}
                    {detailsModalType === 'partially-approved' && (
                      <div className="grn-preview-card grn-preview-card-yellow">
                        <h3 className="grn-preview-card-title grn-preview-card-title-yellow">Partial Approval Details</h3>
                        <div className="grn-preview-card-content">
                          <div className="grn-preview-info-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                            <div>
                              <p className="grn-preview-label">Approved By</p>
                              <p className="grn-preview-value">{selectedGRN.approved_by || 'N/A'}</p>
                            </div>
                            {selectedGRN.approved_by_designation && (
                              <div>
                                <p className="grn-preview-label">Designation</p>
                                <p className="grn-preview-value">{selectedGRN.approved_by_designation}</p>
                              </div>
                            )}
                            <div>
                              <p className="grn-preview-label">Approved Date</p>
                              <p className="grn-preview-value">{formatDate(selectedGRN.approved_date) || 'N/A'}</p>
                            </div>
                            {selectedGRN.follow_up_action && (
                              <div>
                                <p className="grn-preview-label">Follow-up Action</p>
                                <p className="grn-preview-value">{getFollowUpActionLabel(selectedGRN.follow_up_action)}</p>
                              </div>
                            )}
                            {selectedGRN.approval_notes && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <p className="grn-preview-label">Approval Notes</p>
                                <p className="grn-preview-value">{selectedGRN.approval_notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Rejection Details */}
                    {detailsModalType === 'rejected' && (
                      <div className="grn-preview-card grn-preview-card-red">
                        <h3 className="grn-preview-card-title grn-preview-card-title-red">Rejection Details</h3>
                        <div className="grn-preview-card-content">
                          <div className="grn-preview-info-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
                            <div>
                              <p className="grn-preview-label">Rejected By</p>
                              <p className="grn-preview-value">{selectedGRN.rejected_by || 'N/A'}</p>
                            </div>
                            {selectedGRN.rejected_by_designation && (
                              <div>
                                <p className="grn-preview-label">Designation</p>
                                <p className="grn-preview-value">{selectedGRN.rejected_by_designation}</p>
                              </div>
                            )}
                            <div>
                              <p className="grn-preview-label">Rejected Date</p>
                              <p className="grn-preview-value">{formatDate(selectedGRN.rejected_date) || 'N/A'}</p>
                            </div>
                            {selectedGRN.follow_up_action && (
                              <div>
                                <p className="grn-preview-label">Follow-up Action</p>
                                <p className="grn-preview-value">{getFollowUpActionLabel(selectedGRN.follow_up_action)}</p>
                              </div>
                            )}
                            {selectedGRN.rejection_reason && (
                              <div style={{ gridColumn: '1 / -1' }}>
                                <p className="grn-preview-label" style={{ color: '#DC2626' }}>Rejection Reason</p>
                                <p className="grn-preview-value">{selectedGRN.rejection_reason}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Items Table */}
                    {selectedGRN.items && selectedGRN.items.length > 0 && (
                      <div className="grn-preview-table-section">
                        <div className="grn-preview-table-wrapper">
                          <table className="grn-preview-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>SKU</th>
                                <th>Description</th>
                                <th>HSN</th>
                                <th className="grn-text-center">UOM</th>
                                <th className="grn-text-right">Ordered</th>
                                <th className="grn-text-right">Received</th>
                                <th className="grn-text-right">Accepted</th>
                                <th className="grn-text-right">Rejected</th>
                                <th>Batch/Lot</th>
                                <th>Condition</th>
                                <th>Remarks</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedGRN.items.map((item, index) => (
                                <tr key={item.id || index}>
                                  <td>{index + 1}</td>
                                  <td>{item.sku_item_code || '-'}</td>
                                  <td>{item.description || '-'}</td>
                                  <td>{item.hsn || '-'}</td>
                                  <td className="grn-text-center">{getUnitLabel(item.uom) || '-'}</td>
                                  <td className="grn-text-right">{item.qty_ordered || '0'}</td>
                                  <td className="grn-text-right">{item.qty_received || '0'}</td>
                                  <td className="grn-text-right grn-text-green">{item.qty_accepted || '0'}</td>
                                  <td className="grn-text-right grn-text-red">{item.qty_rejected || '0'}</td>
                                  <td>{item.batch_lot || '-'}</td>
                                  <td>
                                    <span className={`grn-preview-badge grn-preview-badge-${(item.condition || 'good').toLowerCase()}`}>
                                      {item.condition || 'Good'}
                                    </span>
                                  </td>
                                  <td className="grn-text-small">{item.remarks || ''}</td>
                                </tr>
                              ))}
                              <tr className="grn-preview-table-total">
                                <td colSpan="1"></td>
                                <td className="grn-text-right"><strong>Totals:</strong></td>
                                <td colSpan="3"></td>
                                <td className="grn-text-right"><strong>{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.qty_ordered) || 0), 0)}</strong></td>
                                <td className="grn-text-right"><strong>{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.qty_received) || 0), 0)}</strong></td>
                                <td className="grn-text-right grn-text-green"><strong>{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.qty_accepted) || 0), 0)}</strong></td>
                                <td className="grn-text-right grn-text-red"><strong>{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.qty_rejected) || 0), 0)}</strong></td>
                                <td colSpan="4"></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grn-preview-modal-footer">
                    <button className="grn-btn grn-btn-secondary" onClick={handleCloseModal}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Comprehensive GRN Details Modal (Similar to Preview Popup) */}
        {isGRNDetailsModalOpen && selectedGRN && selectedManifest && (
          <div className="grn-manifest-list-modal-wrapper">
            <div className="grn-generate">
              <div className="grn-preview-modal-overlay" onClick={handleCloseModal}>
              <div 
                className="grn-preview-modal" 
                role="dialog"
                aria-modal="true"
                aria-labelledby="grn-details-title"
                onClick={(e) => e.stopPropagation()}
                tabIndex="-1"
              >
              <button 
                className="grn-preview-modal-close" 
                onClick={handleCloseModal}
                aria-label="Close GRN details"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>

              <div className="grn-preview-modal-header">
                <div>
                  <h2 id="grn-details-title" className="grn-preview-modal-title">GRN Details - {selectedGRN.grn_number || 'N/A'}</h2>
                  <p className="grn-preview-modal-subtitle">View complete Goods Received Note information</p>
                </div>
              </div>

              <div className="grn-preview-content" id="grn-details-content">
                {/* GRN Header */}
                <div className="grn-preview-header-section">
                  <div className="grn-preview-header-top">
                    <div>
                      <h1 className="grn-preview-main-title">GOODS RECEIVED NOTE</h1>
                      <p className="grn-preview-doc-info">Document Type: GRN | Status: {selectedGRN.status || 'N/A'}</p>
                    </div>
                    <div className="grn-preview-header-right">
                      <p className="grn-preview-grn-number"><strong>GRN No:</strong> {selectedGRN.grn_number || 'N/A'}</p>
                      <p className="grn-preview-date">Date: {selectedGRN.grn_date ? formatDate(selectedGRN.grn_date) : 'N/A'}</p>
                      <p className="grn-preview-po">PO: {selectedGRN.po_number || selectedManifest.po_number || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Supplier & Delivery Info Grid */}
                <div className="grn-preview-info-grid">
                  <div className="grn-preview-card grn-preview-card-orange">
                    <h3 className="grn-preview-card-title grn-preview-card-title-orange">Supplier Details</h3>
                    <div className="grn-preview-card-content">
                      <p><strong>Name:</strong> {selectedGRN.supplier_name || 'N/A'}</p>
                      <p><strong>GSTIN:</strong> {selectedGRN.supplier_gstin || 'N/A'}</p>
                      <p><strong>Address:</strong> {selectedGRN.address || 'N/A'}</p>
                      <p><strong>Contact:</strong> {selectedGRN.contact_person || 'N/A'} {selectedGRN.phone ? `(${selectedGRN.phone})` : ''}</p>
                    </div>
                  </div>
                  <div className="grn-preview-card grn-preview-card-purple">
                    <h3 className="grn-preview-card-title grn-preview-card-title-purple">Delivery Information</h3>
                    <div className="grn-preview-card-content">
                      <p><strong>Delivery Challan:</strong> {selectedGRN.delivery_challan_no || 'N/A'}</p>
                      <p><strong>Invoice No:</strong> {selectedGRN.invoice_number || 'N/A'}</p>
                      <p><strong>Manifest Number:</strong> {selectedGRN.manifest_number || selectedManifest.manifest_number || 'N/A'}</p>
                      <p><strong>Delivery Date:</strong> {selectedGRN.delivery_date ? formatDate(selectedGRN.delivery_date) : 'N/A'}</p>
                      <p><strong>Vehicle:</strong> {selectedGRN.vehicle_number || 'N/A'} {selectedGRN.driver_name ? `| Driver: ${selectedGRN.driver_name}` : ''}</p>
                      <p><strong>Driver Contact:</strong> {selectedGRN.driver_contact || 'N/A'}</p>
                      <p><strong>LR Number:</strong> {selectedGRN.lr_number || 'N/A'}</p>
                      <p><strong>Transport:</strong> {selectedGRN.transport_company || 'N/A'}</p>
                      <p><strong>Received By:</strong> {selectedGRN.received_by_name || 'Not specified'}</p>
                      <p><strong>Warehouse:</strong> {selectedGRN.warehouse_location || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Received Items Table */}
                {selectedGRN.items && selectedGRN.items.length > 0 && (
                  <div className="grn-preview-table-section">
                    <div className="grn-preview-table-wrapper">
                      <table className="grn-preview-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>SKU</th>
                            <th>Description</th>
                            <th>HSN</th>
                            <th className="grn-text-center">UOM</th>
                            <th className="grn-text-right">Ordered</th>
                            <th className="grn-text-right">Received</th>
                            <th className="grn-text-right">Accepted</th>
                            <th className="grn-text-right">Rejected</th>
                            <th>Batch/Lot</th>
                            <th>Mfg Date</th>
                            <th>Exp Date</th>
                            <th>Condition</th>
                            <th>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedGRN.items.map((item, index) => (
                            <tr key={item.id || index}>
                              <td>{index + 1}</td>
                              <td>{item.sku_item_code || '-'}</td>
                              <td>{item.description || '-'}</td>
                              <td>{item.hsn || '-'}</td>
                              <td className="grn-text-center">{getUnitLabel(item.uom) || '-'}</td>
                              <td className="grn-text-right">{item.qty_ordered || '0'}</td>
                              <td className="grn-text-right">{item.qty_received || '0'}</td>
                              <td className="grn-text-right grn-text-green">{item.qty_accepted || '0'}</td>
                              <td className="grn-text-right grn-text-red">{item.qty_rejected || '0'}</td>
                              <td>{item.batch_lot || '-'}</td>
                              <td>{item.mfg_date ? formatDate(item.mfg_date) : '-'}</td>
                              <td>{item.exp_date ? formatDate(item.exp_date) : '-'}</td>
                              <td>
                                <span className={`grn-preview-badge grn-preview-badge-${(item.condition || 'good').toLowerCase()}`}>
                                  {item.condition || 'Good'}
                                </span>
                              </td>
                              <td className="grn-text-small">{item.remarks || ''}</td>
                            </tr>
                          ))}
                          <tr className="grn-preview-table-total">
                            <td colSpan="1"></td>
                            <td className="grn-text-right"><strong>Totals:</strong></td>
                            <td colSpan="3"></td>
                            <td className="grn-text-right"><strong>{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.qty_ordered) || 0), 0)}</strong></td>
                            <td className="grn-text-right"><strong>{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.qty_received) || 0), 0)}</strong></td>
                            <td className="grn-text-right grn-text-green"><strong>{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.qty_accepted) || 0), 0)}</strong></td>
                            <td className="grn-text-right grn-text-red"><strong>{selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.qty_rejected) || 0), 0)}</strong></td>
                            <td colSpan="5"></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Physical Verification Summary */}
                <div className="grn-preview-card grn-preview-card-indigo">
                  <h3 className="grn-preview-card-title grn-preview-card-title-indigo">Physical Verification Summary</h3>
                  <div className="grn-preview-verification-grid">
                    <div>
                      <p className="grn-preview-label">Packages Expected</p>
                      <p className="grn-preview-value">{selectedGRN.packages_expected || '0'}</p>
                    </div>
                    <div>
                      <p className="grn-preview-label">Packages Received</p>
                      <p className="grn-preview-value">{selectedGRN.packages_received || '0'}</p>
                    </div>
                    <div>
                      <p className="grn-preview-label">Weight Expected (kg)</p>
                      <p className="grn-preview-value">{selectedGRN.weight_expected_kg || '0'}</p>
                    </div>
                    <div>
                      <p className="grn-preview-label">Weight Received (kg)</p>
                      <p className="grn-preview-value">{selectedGRN.weight_received_kg || '0'}</p>
                    </div>
                  </div>
                  <div className="grn-preview-verification-checks">
                    <p className={selectedGRN.dimensions_verified ? 'grn-check-verified' : 'grn-check-unverified'}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                      Dimensions Verified
                    </p>
                    <p className={selectedGRN.packaging_integrity_ok ? 'grn-check-verified' : 'grn-check-unverified'}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                      Packaging Integrity
                    </p>
                    <p className={selectedGRN.seals_verified ? 'grn-check-verified' : 'grn-check-unverified'}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                        <circle cx="12" cy="12" r="10"></circle>
                        <path d="m9 12 2 2 4-4"></path>
                      </svg>
                      Seals Verified
                    </p>
                    {selectedGRN.seals_verified && selectedGRN.seal_numbers && (
                      <p className="grn-preview-seal-numbers">Seal Nos: {selectedGRN.seal_numbers}</p>
                    )}
                  </div>
                </div>

                {/* Quality Inspection & Discrepancies Grid */}
                <div className="grn-preview-info-grid">
                  <div className="grn-preview-card grn-preview-card-green">
                    <h3 className="grn-preview-card-title grn-preview-card-title-green">Quality Inspection</h3>
                    <div className="grn-preview-card-content">
                      {(() => {
                        const qualityChecklist = selectedGRN.quality_inspection_checklist 
                          ? (typeof selectedGRN.quality_inspection_checklist === 'string' 
                            ? JSON.parse(selectedGRN.quality_inspection_checklist) 
                            : selectedGRN.quality_inspection_checklist)
                          : {};
                        
                        const hasAnyCheck = qualityChecklist.packagingIntact || qualityChecklist.correctLabeling || 
                          qualityChecklist.noVisibleDamage || qualityChecklist.quantityMatches || 
                          qualityChecklist.meetsQualityStandards || qualityChecklist.allDocumentsComplete;
                        
                        return (
                          <>
                            {qualityChecklist.packagingIntact && (
                              <p className="grn-check-verified">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <path d="m9 12 2 2 4-4"></path>
                                </svg>
                                Packaging Intact
                              </p>
                            )}
                            {qualityChecklist.correctLabeling && (
                              <p className="grn-check-verified">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <path d="m9 12 2 2 4-4"></path>
                                </svg>
                                Correct Labeling
                              </p>
                            )}
                            {qualityChecklist.noVisibleDamage && (
                              <p className="grn-check-verified">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <path d="m9 12 2 2 4-4"></path>
                                </svg>
                                No Visible Damage
                              </p>
                            )}
                            {qualityChecklist.quantityMatches && (
                              <p className="grn-check-verified">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <path d="m9 12 2 2 4-4"></path>
                                </svg>
                                Quantity Matches
                              </p>
                            )}
                            {qualityChecklist.meetsQualityStandards && (
                              <p className="grn-check-verified">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <path d="m9 12 2 2 4-4"></path>
                                </svg>
                                Quality Standards Met
                              </p>
                            )}
                            {qualityChecklist.allDocumentsComplete && (
                              <p className="grn-check-verified">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon">
                                  <circle cx="12" cy="12" r="10"></circle>
                                  <path d="m9 12 2 2 4-4"></path>
                                </svg>
                                Documents Complete
                              </p>
                            )}
                            {!hasAnyCheck && (
                              <p className="grn-check-unverified">No quality inspection items selected</p>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="grn-preview-inspector-info">
                      <p><strong>Inspector:</strong> {selectedGRN.inspector_name || 'Not specified'}</p>
                      <p><strong>Date:</strong> {selectedGRN.inspection_date ? formatDate(selectedGRN.inspection_date) : 'N/A'}</p>
                      {selectedGRN.defect_issue_description && (
                        <p><strong>Defect / Issue Description:</strong> {selectedGRN.defect_issue_description}</p>
                      )}
                    </div>
                  </div>
                  <div className="grn-preview-card grn-preview-card-red">
                    <h3 className="grn-preview-card-title grn-preview-card-title-red">Discrepancies & Issues</h3>
                    <div className="grn-preview-discrepancies-content">
                      {(selectedGRN.shortage_detected || selectedGRN.damage_detected || selectedGRN.quality_issue) ? (
                        <div>
                          {selectedGRN.shortage_detected && selectedGRN.additional_notes && (
                            <div className="grn-preview-discrepancy-item">
                              <p><strong>Shortage Detected:</strong> {selectedGRN.additional_notes}</p>
                            </div>
                          )}
                          {selectedGRN.damage_detected && selectedGRN.additional_notes && (
                            <div className="grn-preview-discrepancy-item">
                              <p><strong>Damage Detected:</strong> {selectedGRN.additional_notes}</p>
                            </div>
                          )}
                          {selectedGRN.quality_issue && selectedGRN.defect_issue_description && (
                            <div className="grn-preview-discrepancy-item">
                              <p><strong>Quality Issue:</strong> {selectedGRN.defect_issue_description}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="grn-preview-no-discrepancies">
                          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="grn-check-icon-large">
                            <circle cx="12" cy="12" r="10"></circle>
                            <path d="m9 12 2 2 4-4"></path>
                          </svg>
                          <p className="grn-preview-no-discrepancies-text">No discrepancies reported</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Additional Notes / Remarks */}
                {selectedGRN.additional_notes && (
                  <div className="grn-preview-card grn-preview-card-gray">
                    <h3 className="grn-preview-card-title grn-preview-card-title-gray">Additional Notes / Remarks</h3>
                    <div className="grn-preview-card-content">
                      <p>{selectedGRN.additional_notes}</p>
                    </div>
                  </div>
                )}

                {/* Summary Stats */}
                {selectedGRN.items && selectedGRN.items.length > 0 && (
                  <div className="grn-preview-summary-stats">
                    <div className="grn-preview-summary-item grn-preview-summary-blue">
                      <p className="grn-preview-summary-label">Total Items</p>
                      <p className="grn-preview-summary-value">{selectedGRN.items.length}</p>
                    </div>
                    <div className="grn-preview-summary-item grn-preview-summary-green">
                      <p className="grn-preview-summary-label">Accepted</p>
                      <p className="grn-preview-summary-value">{selectedGRN.total_qty_accepted || selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.qty_accepted) || 0), 0)}</p>
                    </div>
                    <div className="grn-preview-summary-item grn-preview-summary-red">
                      <p className="grn-preview-summary-label">Rejected</p>
                      <p className="grn-preview-summary-value">{selectedGRN.total_qty_rejected || selectedGRN.items.reduce((sum, item) => sum + (parseFloat(item.qty_rejected) || 0), 0)}</p>
                    </div>
                    <div className="grn-preview-summary-item grn-preview-summary-orange">
                      <p className="grn-preview-summary-label">Acceptance Rate</p>
                      <p className="grn-preview-summary-value">{selectedGRN.acceptance_rate || '0'}%</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="grn-preview-modal-footer">
                <button className="grn-btn grn-btn-secondary" onClick={handleCloseModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
            </div>
          </div>
        )}
      </div>

      <SupplierRatingModal
        open={Boolean(ratingModalManifest)}
        onClose={() => setRatingModalManifest(null)}
        purchaseOrderId={ratingModalManifest?.purchaseOrderId}
        grnId={ratingModalManifest?.grnId}
        supplierName={ratingModalManifest?.exporter}
        poNumber={ratingModalManifest?.poNumber}
        onSuccess={handleRatingSubmitted}
      />
    </div>
  );
}
